import { apiClient } from '@/api/client';
import type {
  BillingChange,
  BillingOverview,
  BillingPortalSession,
  CheckoutCatalogue,
  CheckoutQuote,
  CheckoutQuoteParams,
  CheckoutReturn,
  CheckoutSession,
  Invoice,
  UpcomingInvoice,
} from '@/lib/types/billing';

/**
 * Billing and checkout calls for the acting startup (FR-39, FR-41).
 *
 * Every route is entity-scoped (`/startups/:entityId/billing`) because a
 * subscription belongs to the STARTUP, not to the person holding the session -
 * the same reason the purchase screen names the startup. The caller passes the
 * entity id explicitly rather than this module reading the session, so a route
 * can never be built against a different entity from the one the page renders.
 */

/** Base path for one startup's billing resource. */
function base(entityId: string): string {
  return `/startups/${entityId}/billing`;
}

/** The plan catalogue and any subscription this startup already holds (FR-39). */
export async function getCheckoutCatalogue(entityId: string): Promise<CheckoutCatalogue> {
  const res = await apiClient.get<CheckoutCatalogue>(`${base(entityId)}/checkout`);
  return res.data;
}

/**
 * Prices a purchase for the review step (Sprint 7 QA). Reads only: nothing is
 * reserved, and a promo code is not redeemed until the purchase completes.
 */
export async function getCheckoutQuote(
  entityId: string,
  params: CheckoutQuoteParams,
): Promise<CheckoutQuote> {
  const res = await apiClient.get<CheckoutQuote>(`${base(entityId)}/checkout/quote`, { params });
  return res.data;
}

/**
 * Opens a Stripe Checkout session.
 *
 * Returns the URL rather than navigating: the caller decides when the browser
 * leaves, and a test can assert the request without a jsdom navigation.
 */
export async function createCheckoutSession(
  entityId: string,
  body: { planId: string; promoCode?: string },
): Promise<CheckoutSession> {
  const res = await apiClient.post<CheckoutSession>(`${base(entityId)}/checkout`, body);
  return res.data;
}

/**
 * What actually happened (A-73).
 *
 * Takes no session id: the backend resolves the state from the STARTUP's own
 * subscription, not from the Stripe session. The `?session=` Stripe puts on the
 * return URL is the SPA's signal that the member came back from a completed
 * checkout rather than abandoning one - it is not an argument to this call.
 *
 * `pending` is a real answer here, not a loading state: the confirming webhook
 * runs on a queue and may not have landed yet.
 */
export async function getCheckoutReturn(entityId: string): Promise<CheckoutReturn> {
  const res = await apiClient.get<CheckoutReturn>(`${base(entityId)}/checkout/return`);
  return res.data;
}

/** Current plan, status, dates and seat figures (FR-41). */
export async function getBillingOverview(entityId: string): Promise<BillingOverview> {
  const res = await apiClient.get<BillingOverview>(base(entityId));
  return res.data;
}

/** The native billing history. The PDF behind each row is Stripe's (A-72). */
export async function listInvoices(entityId: string): Promise<Invoice[]> {
  const res = await apiClient.get<Invoice[]>(`${base(entityId)}/invoices`);
  return res.data;
}

/** A one-shot Stripe billing-portal session, for card management only (A-72). */
export async function createPortalSession(entityId: string): Promise<BillingPortalSession> {
  const res = await apiClient.post<BillingPortalSession>(`${base(entityId)}/portal-session`);
  return res.data;
}

/** The next renewal invoice, from Stripe's preview; null when nothing will renew. */
export async function getUpcomingInvoice(entityId: string): Promise<UpcomingInvoice | null> {
  const res = await apiClient.get<UpcomingInvoice | null>(`${base(entityId)}/upcoming-invoice`);
  return res.data;
}

/** Turns auto-renewal on or off. Takes effect at period end, never at the click (A-74). */
export async function updateAutoRenew(
  entityId: string,
  autoRenew: boolean,
): Promise<BillingChange> {
  const res = await apiClient.patch<BillingChange>(`${base(entityId)}/auto-renew`, { autoRenew });
  return res.data;
}

/**
 * Schedules cancellation at period end.
 *
 * DELETE, but nothing is deleted: access continues to `accessEndsAt` and the
 * subscription stays reactivatable until then (A-74). The response carries
 * both dates so the confirmation can state them.
 */
export async function cancelSubscription(entityId: string): Promise<BillingChange> {
  const res = await apiClient.delete<BillingChange>(base(entityId));
  return res.data;
}
